import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Drug {
  id: string;
  drugName: string;
  activeIngredient: string;
  companyName: string;
  type: 'NDA' | 'sNDA';
  category: string;
  therapeuticArea: string;
  indication: string;
  pdufaDate: string;
  peakSales?: number;
  routeOfAdministration?: string;
  dosageForm?: string;
  strength?: string;
  dosingRegimen?: string;
  comments?: string;
  linkedNda?: string;
  designations?: string[];
  status?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface Filters {
  period?: { type: string; value: string };
  type?: 'NDA' | 'sNDA';
  category?: string;
  categories?: string[]; // For multiple selection (sNDA)
  subCategory?: string;
  subCategories?: string[]; // For multiple EAM subcategory selection
  therapeuticArea?: string;
  therapeuticAreas?: string[]; // For multiple therapeutic area selection
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private drugsSubject = new BehaviorSubject<Drug[]>([]);
  drugs$ = this.drugsSubject.asObservable();

  // Static sample data
  private staticDrugs: Drug[] = [
    {
      id: '1',
      drugName: 'Adalimumab',
      activeIngredient: 'Adalimumab',
      companyName: 'AbbVie',
      type: 'NDA',
      category: 'nce',
      therapeuticArea: 'AUTOIMMUNE_DISEASE',
      indication: 'Treatment of rheumatoid arthritis and other autoimmune conditions',
      pdufaDate: '2024-03-15',
      peakSales: 20000000000,
      routeOfAdministration: 'Subcutaneous injection',
      dosageForm: 'Pre-filled syringe',
      strength: '40mg/0.8mL',
      dosingRegimen: 'Every other week',
      designations: ['Priority Review', 'Breakthrough Therapy Designation'],
      createdAt: '2024-01-01T00:00:00.000Z'
    },
    {
      id: '2',
      drugName: 'Pembrolizumab',
      activeIngredient: 'Pembrolizumab',
      companyName: 'Merck',
      type: 'NDA',
      category: 'nce',
      therapeuticArea: 'ONCOLOGY',
      indication: 'Treatment of melanoma and other solid tumors',
      pdufaDate: '2024-05-20',
      peakSales: 17000000000,
      routeOfAdministration: 'Intravenous',
      dosageForm: 'Injection',
      strength: '100mg/4mL',
      dosingRegimen: 'Every 3 weeks',
      designations: ['Priority Review', 'Breakthrough Therapy Designation', 'Orphan Drug Designation'],
      createdAt: '2024-01-02T00:00:00.000Z'
    },
    {
      id: '3',
      drugName: 'Tofacitinib Extended Release',
      activeIngredient: 'Tofacitinib',
      companyName: 'Pfizer',
      type: 'sNDA',
      category: 'new-dosage',
      therapeuticArea: 'RHEUMATOLOGY',
      indication: 'Treatment of rheumatoid arthritis - extended release formulation',
      pdufaDate: '2024-07-10',
      peakSales: 2500000000,
      routeOfAdministration: 'Oral',
      dosageForm: 'Extended release tablet',
      strength: '11mg',
      dosingRegimen: 'Once daily',
      linkedNda: '1',
      designations: ['Standard Review'],
      createdAt: '2024-01-03T00:00:00.000Z'
    },
    {
      id: '4',
      drugName: 'Nivolumab',
      activeIngredient: 'Nivolumab',
      companyName: 'Bristol Myers Squibb',
      type: 'NDA',
      category: 'nce',
      therapeuticArea: 'ONCOLOGY',
      indication: 'Treatment of non-small cell lung cancer',
      pdufaDate: '2024-04-25',
      peakSales: 7500000000,
      routeOfAdministration: 'Intravenous',
      dosageForm: 'Injection',
      strength: '240mg/24mL',
      dosingRegimen: 'Every 2 weeks',
      designations: ['Priority Review', 'Fast Track Designation'],
      createdAt: '2024-01-04T00:00:00.000Z'
    },
    {
      id: '5',
      drugName: 'Semaglutide',
      activeIngredient: 'Semaglutide',
      companyName: 'Novo Nordisk',
      type: 'NDA',
      category: 'nce',
      therapeuticArea: 'ENDOCRINOLOGY',
      indication: 'Treatment of type 2 diabetes mellitus',
      pdufaDate: '2024-06-30',
      peakSales: 4200000000,
      routeOfAdministration: 'Subcutaneous',
      dosageForm: 'Pre-filled pen',
      strength: '0.5mg, 1mg',
      dosingRegimen: 'Once weekly',
      designations: ['Standard Review'],
      createdAt: '2024-01-05T00:00:00.000Z'
    },
    {
      id: '6',
      drugName: 'Upadacitinib Pediatric',
      activeIngredient: 'Upadacitinib',
      companyName: 'AbbVie',
      type: 'sNDA',
      category: 'new-population',
      therapeuticArea: 'RHEUMATOLOGY',
      indication: 'Treatment of juvenile idiopathic arthritis',
      pdufaDate: '2024-08-15',
      peakSales: 1800000000,
      routeOfAdministration: 'Oral',
      dosageForm: 'Extended release tablet',
      strength: '15mg, 30mg',
      dosingRegimen: 'Once daily',
      linkedNda: '1',
      designations: ['Priority Review', 'Rare Pediatric Disease Designation'],
      createdAt: '2024-01-06T00:00:00.000Z'
    }
  ];

  constructor() {
    this.drugsSubject.next(this.staticDrugs);
  }

  getDrugs(): Observable<Drug[]> {
    return this.drugs$;
  }

  getFilteredDrugs(filters: Filters): Observable<Drug[]> {
    return new Observable(observer => {
      let filteredDrugs = [...this.staticDrugs];

      if (filters.type) {
        filteredDrugs = filteredDrugs.filter(drug => drug.type === filters.type);
      }

      if (filters.category) {
        filteredDrugs = filteredDrugs.filter(drug => drug.category === filters.category);
      }

      if (filters.therapeuticArea) {
        filteredDrugs = filteredDrugs.filter(drug => drug.therapeuticArea === filters.therapeuticArea);
      }

      if (filters.period) {
        filteredDrugs = this.filterByTimePeriod(filteredDrugs, filters.period);
      }

      filteredDrugs.sort((a, b) => new Date(a.pdufaDate).getTime() - new Date(b.pdufaDate).getTime());

      observer.next(filteredDrugs);
      observer.complete();
    });
  }

  private filterByTimePeriod(drugs: Drug[], period: { type: string; value: string }): Drug[] {
    if (!period || !period.type || !period.value) {
      return drugs;
    }

    const now = new Date();
    let startDate: Date, endDate: Date;

    switch (period.type) {
      case 'monthly':
        const [year, month] = period.value.split('-');
        startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        endDate = new Date(parseInt(year), parseInt(month), 0);
        break;

      case 'quarterly':
        const [qYear, quarter] = period.value.split('-Q');
        const quarterNum = parseInt(quarter);
        const startMonth = (quarterNum - 1) * 3;
        startDate = new Date(parseInt(qYear), startMonth, 1);
        endDate = new Date(parseInt(qYear), startMonth + 3, 0);
        break;

      case 'halfyearly':
        const [hYear, half] = period.value.split('-H');
        const halfNum = parseInt(half);
        const halfStartMonth = (halfNum - 1) * 6;
        startDate = new Date(parseInt(hYear), halfStartMonth, 1);
        endDate = new Date(parseInt(hYear), halfStartMonth + 6, 0);
        break;

      case 'yearly':
        const yearNum = parseInt(period.value);
        startDate = new Date(yearNum, 0, 1);
        endDate = new Date(yearNum, 11, 31);
        break;

      default:
        return drugs;
    }

    return drugs.filter(drug => {
      const pdufaDate = new Date(drug.pdufaDate);
      return pdufaDate >= startDate && pdufaDate <= endDate;
    });
  }

  // saveDrug(drug: Drug): Observable<Drug> {
  //   // return new Observable(observer => {
  //   //   const existingIndex = this.staticDrugs.findIndex(d => d.id === drug.id);
      
  //   //   if (existingIndex >= 0) {
  //   //     this.staticDrugs[existingIndex] = { ...this.staticDrugs[existingIndex], ...drug };
  //   //   } else {
  //   //     this.staticDrugs.push(drug);
  //   //   }
      
  //   //   this.drugsSubject.next(this.staticDrugs);
  //   //   observer.next(drug);
  //   //   observer.complete();
  //   // });
  // }

  getAllNDAs(): Observable<Drug[]> {
    return new Observable(observer => {
      const ndas = this.staticDrugs.filter(drug => drug.type === 'NDA');
      observer.next(ndas);
      observer.complete();
    });
  }

  getStatistics(filters: Filters = {}): Observable<any> {
    return new Observable(observer => {
      this.getFilteredDrugs(filters).subscribe(drugs => {
        const stats = {
          totalDrugs: drugs.length,
          ndaCount: drugs.filter(d => d.type === 'NDA').length,
          sndaCount: drugs.filter(d => d.type === 'sNDA').length,
          priorityReviews: drugs.filter(d => 
            d.designations && d.designations.includes('Priority Review')
          ).length
        };
        observer.next(stats);
        observer.complete();
      });
    });
  }
}